import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { useCallback, useEffect, useRef } from "react";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import {
  $getRoot,
  $insertNodes,
  $isElementNode,
  COMMAND_PRIORITY_EDITOR,
  createCommand,
  type ElementFormatType,
  type LexicalCommand,
  type LexicalNode,
} from "lexical";
import { $createRawHtmlNode } from "./RawHtmlNode";
import type { EditorVariable } from "./VariableNode";

export const RESET_HTML_COMMAND: LexicalCommand<string | null | undefined> =
  createCommand("RESET_HTML_COMMAND");

const QUILL_ALIGN: Record<string, ElementFormatType> = {
  "ql-align-right": "right",
  "ql-align-center": "center",
  "ql-align-justify": "justify",
};
const RAW_BLOCK_RE =
  /<div\s+class=['"]card['"][\s\S]*?<\/table>\s*<\/div>|<table[\s\S]*?<\/table>|<img\s[^>]*class=['"](?:signature|logo)['"][^>]*\/?>|<svg[\s\S]*?<\/svg>/gi;
const PLACEHOLDER = "\uFFFC";
const VALID_ALIGNMENTS: ReadonlyArray<ElementFormatType> = [
  "left",
  "center",
  "right",
  "justify",
];

function extractRawBlocks(html: string): { cleaned: string; blocks: string[] } {
  const blocks: string[] = [];
  const cleaned = html.replace(RAW_BLOCK_RE, (match) => {
    blocks.push(match);
    return `<p>${PLACEHOLDER}</p>`;
  });
  return { cleaned, blocks };
}

function convertQuillClasses(dom: Document): void {
  dom.querySelectorAll<HTMLElement>("[class*='ql-']").forEach((el) => {
    for (const cls of Array.from(el.classList)) {
      const align = QUILL_ALIGN[cls];
      if (align) {
        el.style.textAlign = align;
        el.classList.remove(cls);
      }
      const m = cls.match(/ql-indent-(\d)/);
      if (m) {
        el.style.paddingLeft = `${parseInt(m[1], 10) * 3}em`;
        el.classList.remove(cls);
      }
    }
  });
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function wrapVariableTokens(
  dom: Document,
  variables: ReadonlyArray<EditorVariable>,
): void {
  if (variables.length === 0) return;
  const source = `\\{(${variables
    .map((v) => escapeRegExp(v.key))
    .join("|")})\\}`;

  const textNodes: Text[] = [];
  const walker = dom.createTreeWalker(dom.body, NodeFilter.SHOW_TEXT);
  let current: Node | null = walker.nextNode();
  while (current) {
    let inside = false;
    let p = current.parentElement;
    while (p) {
      if (p.hasAttribute("data-variable")) {
        inside = true;
        break;
      }
      p = p.parentElement;
    }
    if (!inside) textNodes.push(current as Text);
    current = walker.nextNode();
  }

  for (const textNode of textNodes) {
    const text = textNode.textContent || "";
    if (!text.includes("{")) continue;
    const re = new RegExp(source, "g");
    const frag = dom.createDocumentFragment();
    let lastIdx = 0;
    let match: RegExpExecArray | null = re.exec(text);
    while (match) {
      if (match.index > lastIdx) {
        frag.appendChild(dom.createTextNode(text.slice(lastIdx, match.index)));
      }
      const span = dom.createElement("span");
      span.setAttribute("data-variable", match[1]);
      span.textContent = `{${match[1]}}`;
      frag.appendChild(span);
      lastIdx = match.index + match[0].length;
      match = re.exec(text);
    }
    if (lastIdx === 0) continue;
    if (lastIdx < text.length) {
      frag.appendChild(dom.createTextNode(text.slice(lastIdx)));
    }
    textNode.parentNode?.replaceChild(frag, textNode);
  }
}

function applyAlignment(nodes: LexicalNode[]): void {
  for (const node of nodes) {
    if (!$isElementNode(node)) continue;
    const style = node.getStyle();
    const m = style.match(/text-align:\s*(left|center|right|justify)/);
    if (!m) continue;
    const candidate = m[1] as ElementFormatType;
    if (VALID_ALIGNMENTS.includes(candidate)) {
      node.setFormat(candidate);
    }
  }
}

interface Props {
  defaultValue?: string;
  onChange: (html: string) => void;
  availableVariables?: ReadonlyArray<EditorVariable>;
}

export default function HtmlPlugin({
  defaultValue,
  onChange,
  availableVariables,
}: Props) {
  const [editor] = useLexicalComposerContext();
  const initRef = useRef(false);
  const updatingRef = useRef(false);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const loadHtml = useCallback(
    (html: string) => {
      const { cleaned, blocks } = extractRawBlocks(html);

      editor.update(() => {
        updatingRef.current = true;
        const root = $getRoot();
        root.clear();
        const dom = new DOMParser().parseFromString(cleaned, "text/html");
        convertQuillClasses(dom);
        if (availableVariables) wrapVariableTokens(dom, availableVariables);
        const nodes = $generateNodesFromDOM(editor, dom);
        applyAlignment(nodes);

        const result: LexicalNode[] = [];
        let idx = 0;
        for (const node of nodes) {
          if (
            node.getTextContent().includes(PLACEHOLDER) &&
            idx < blocks.length
          ) {
            result.push($createRawHtmlNode(blocks[idx++]));
          } else {
            result.push(node);
          }
        }

        $insertNodes(result);
        updatingRef.current = false;
      });
    },
    [editor, availableVariables],
  );

  useEffect(() => {
    if (initRef.current || !defaultValue) return;
    initRef.current = true;
    loadHtml(defaultValue);
  }, [defaultValue, loadHtml]);

  useEffect(() => {
    return editor.registerCommand(
      RESET_HTML_COMMAND,
      (html) => {
        loadHtml(html ?? defaultValue ?? "");
        return true;
      },
      COMMAND_PRIORITY_EDITOR,
    );
  }, [editor, loadHtml, defaultValue]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      if (updatingRef.current) return;
      editorState.read(() =>
        onChangeRef.current($generateHtmlFromNodes(editor)),
      );
    });
  }, [editor]);

  return null;
}
